import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Restaurant, RestaurantImage } from "@/lib/types";
import { queryClient, safeParseResponse } from "@/lib/queryClient";
import { queryKeys, invalidateRestaurantDetail, invalidateAllRestaurantLists } from "@/lib/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { normalizeImagePath } from "@/lib/utils";
import { 
  Loader2, Upload, Trash2, Image as ImageIcon, 
  XCircle, Check, AlertCircle, Crown, Star, Camera,
  Plus, MoreVertical, ArrowUp, ArrowDown, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface RestaurantFormMediaProps {
  restaurant: Restaurant;
  onDirtyChange: (isDirty: boolean) => void;
  onSavingChange: (isSaving: boolean) => void;
}

const RestaurantFormMedia = ({ restaurant, onDirtyChange, onSavingChange }: RestaurantFormMediaProps) => {
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);

  const { data: images = [], isLoading, refetch } = useQuery<RestaurantImage[]>({
    queryKey: queryKeys.restaurants.images(restaurant.id),
    queryFn: async () => {
      const res = await fetch(`/api/restaurants/${restaurant.id}/images`, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: Boolean(restaurant.id && restaurant.id !== 'new'),
    staleTime: 30 * 1000,
  });

  // Split images: main (profile) vs gallery
  const mainImage = images.find(img => img.sortOrder === 1) || images[0] || null;
  const galleryImages = images.filter(img => img !== mainImage).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  useEffect(() => {
    onDirtyChange(uploadedImages.length > 0);
  }, [uploadedImages, onDirtyChange]);

  // === MUTATIONS ===

  const uploadImagesMutation = useMutation({
    mutationFn: async ({ restaurantId, imageFiles }: { restaurantId: string, imageFiles: File[] }) => {
      const formData = new FormData();
      imageFiles.forEach(file => formData.append('images', file));
      const response = await fetch(`/api/restaurants/${restaurantId}/upload-images`, { method: 'POST', body: formData });
      if (!response.ok) {
        const ct = response.headers.get('Content-Type');
        if (ct?.includes('text/html')) throw new Error(`Server-Fehler (${response.status})`);
        const errorData = await safeParseResponse<{ error?: string }>(response);
        throw new Error(errorData?.error || `HTTP ${response.status}`);
      }
      return await safeParseResponse(response);
    },
    onSuccess: () => {
      toast({ title: "✨ Bilder hochgeladen", description: "Die Bilder wurden erfolgreich hochgeladen." });
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.images(restaurant.id), refetchType: 'all' });
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: false });
      invalidateAllRestaurantLists(queryClient);
      setUploadedImages([]); setImagePreviews([]);
      if (imageFileInputRef.current) imageFileInputRef.current.value = "";
      refetch(); onDirtyChange(false);
    },
    onError: (error: Error) => {
      setError(`Fehler beim Hochladen: ${error.message}`);
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    }
  });

  const deleteImageMutation = useMutation({
    mutationFn: async ({ restaurantId, imageId }: { restaurantId: string, imageId: string | number }) => {
      const response = await fetch(`/api/restaurants/${restaurantId}/images/${imageId}`, { method: 'DELETE' });
      if (!response.ok) {
        const ct = response.headers.get('Content-Type');
        if (ct?.includes('text/html')) throw new Error(`Server-Fehler (${response.status})`);
        const errorData = await safeParseResponse<{ error?: string }>(response);
        throw new Error(errorData?.error || `HTTP ${response.status}`);
      }
      return { success: true };
    },
    onSuccess: () => {
      toast({ title: "Bild gelöscht", description: "Das Bild wurde entfernt." });
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.images(restaurant.id), refetchType: 'all' });
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: false });
      invalidateAllRestaurantLists(queryClient);
      refetch(); setIsDeletingImage(null);
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      setIsDeletingImage(null);
    }
  });

  const sortImagesMutation = useMutation({
    mutationFn: async ({ restaurantId, imageId, direction }: { restaurantId: string, imageId: string | number, direction: 'up' | 'down' }) => {
      const response = await fetch(`/api/restaurants/${restaurantId}/images/${imageId}/sort`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ direction }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await safeParseResponse(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.images(restaurant.id), refetchType: 'all' });
      refetch();
    },
  });

  const setMainImageMutation = useMutation({
    mutationFn: async ({ restaurantId, imageId }: { restaurantId: string, imageId: string | number }) => {
      const response = await fetch(`/api/restaurants/${restaurantId}/images/${imageId}/set-main`, { method: 'POST' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await safeParseResponse(response);
    },
    onSuccess: () => {
      toast({ title: "✅ Profilbild geändert", description: "Das Hauptbild wurde aktualisiert." });
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.images(restaurant.id), refetchType: 'all' });
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: false });
      invalidateAllRestaurantLists(queryClient);
      refetch();
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    }
  });

  // === HANDLERS ===

  const handleFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length !== fileArray.length) {
      toast({ title: "Ungültige Dateien übersprungen", description: "Nur Bilddateien werden akzeptiert.", variant: "destructive" });
    }
    setUploadedImages(prev => [...prev, ...imageFiles]);
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => { if (e.target?.result) setImagePreviews(prev => [...prev, e.target!.result as string]); };
      reader.readAsDataURL(file);
    });
  }, [toast]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleProfileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    // Profile image: upload immediately instead of queuing
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('images', file);
    setIsUploading(true); onSavingChange(true);
    try {
      const response = await fetch(`/api/restaurants/${restaurant.id}/upload-images`, { method: 'POST', body: formData });
      if (!response.ok) {
        const errorData = await safeParseResponse<{ error?: string }>(response);
        throw new Error(errorData?.error || `HTTP ${response.status}`);
      }
      const result = await safeParseResponse<{ images?: Array<{ id: number }> }>(response);
      // If this is the first image (or we want to set as main), set it as profile
      if (result?.images?.[0]?.id) {
        await fetch(`/api/restaurants/${restaurant.id}/images/${result.images[0].id}/set-main`, { method: 'POST' });
      }
      toast({ title: "✨ Profilbild aktualisiert", description: "Das Profilbild wurde erfolgreich hochgeladen." });
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.images(restaurant.id), refetchType: 'all' });
      invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: false });
      invalidateAllRestaurantLists(queryClient);
      refetch();
    } catch (err: any) {
      toast({ title: "Fehler", description: err.message || 'Upload fehlgeschlagen', variant: "destructive" });
    } finally {
      setIsUploading(false); onSavingChange(false);
      if (profileInputRef.current) profileInputRef.current.value = '';
    }
  }, [restaurant.id, toast, refetch, onSavingChange]);

  const uploadImages = async () => {
    if (uploadedImages.length === 0) return;
    setIsUploading(true); onSavingChange(true);
    try { await uploadImagesMutation.mutateAsync({ restaurantId: restaurant.id, imageFiles: uploadedImages }); }
    finally { setIsUploading(false); onSavingChange(false); }
  };

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
          <Button variant="ghost" size="sm" className="ml-auto h-6 px-2" onClick={() => setError(null)}>
            <XCircle className="h-3.5 w-3.5" />
          </Button>
        </Alert>
      )}

      {/* === PROFILBILD SECTION === */}
      <Card className="overflow-hidden border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3 bg-gradient-to-r from-purple-50/50 to-transparent">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Crown className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-base">Profilbild</CardTitle>
              <CardDescription className="text-xs">Das Hauptbild wird in der Restaurant-Übersicht und als Vorschau angezeigt</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            {/* Current profile image */}
            <div className="relative group">
              {mainImage ? (
                <div className="relative">
                  <img
                    src={normalizeImagePath(mainImage.path)}
                    alt="Profilbild"
                    className="h-32 w-32 sm:h-40 sm:w-40 rounded-2xl object-cover ring-4 ring-purple-100 shadow-lg"
                    onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder_header.png'; }}
                  />
                  <Badge className="absolute -top-2 -right-2 bg-purple-600 text-white text-[10px] px-2 py-0.5 shadow-md">
                    <Star className="h-2.5 w-2.5 mr-0.5 fill-current" /> Hauptbild
                  </Badge>
                </div>
              ) : (
                <div className="h-32 w-32 sm:h-40 sm:w-40 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center">
                  <Camera className="h-8 w-8 text-slate-400 mb-1" />
                  <span className="text-xs text-slate-500">Kein Bild</span>
                </div>
              )}
            </div>
            
            {/* Profile actions */}
            <div className="flex-1 text-center sm:text-left space-y-3">
              <div>
                <h4 className="font-medium text-slate-900 text-sm">
                  {mainImage ? 'Profilbild ändern' : 'Profilbild hochladen'}
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Empfohlen: Quadratisch, min. 400×400px. JPG, PNG oder WebP.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                <input ref={profileInputRef} type="file" accept="image/*" onChange={handleProfileUpload} className="hidden" />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => profileInputRef.current?.click()}
                  className="gap-1.5 text-xs h-8 border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {mainImage ? 'Neues Bild' : 'Bild hochladen'}
                </Button>
                {galleryImages.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-1.5 text-xs h-8 text-slate-600">
                        <ImageIcon className="h-3.5 w-3.5" />
                        Aus Galerie wählen
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
                      {galleryImages.map((img) => (
                        <DropdownMenuItem key={img.id} onClick={() => setMainImageMutation.mutate({ restaurantId: restaurant.id, imageId: img.id })}
                          className="gap-2 py-2 cursor-pointer">
                          <img src={normalizeImagePath(img.path)} alt="" className="h-8 w-8 rounded object-cover" onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder_header.png'; }} />
                          <span className="text-xs">Bild #{img.sortOrder || '?'}</span>
                          <Check className="h-3 w-3 ml-auto text-emerald-600" />
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === GALERIE UPLOAD === */}
      <Card className="overflow-hidden border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3 bg-gradient-to-r from-blue-50/50 to-transparent">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <ImageIcon className="h-4 w-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Galerie</CardTitle>
              <CardDescription className="text-xs">Weitere Bilder des Restaurants — werden als Slideshow angezeigt</CardDescription>
            </div>
            <Badge variant="secondary" className="text-xs tabular-nums">
              {images.length} Bild{images.length !== 1 ? 'er' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Drop zone */}
          <div
            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-6 sm:p-8 text-center transition-colors duration-200 cursor-pointer ${
              dragActive 
                ? 'border-primary bg-primary/5 scale-[1.01]' 
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
            }`}
            onClick={() => imageFileInputRef.current?.click()}
          >
            <input ref={imageFileInputRef} type="file" accept="image/*" multiple onChange={(e) => e.target.files && handleFiles(e.target.files)} className="hidden" />
            <div className="flex flex-col items-center gap-2">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center transition-colors ${dragActive ? 'bg-primary/10' : 'bg-slate-100'}`}>
                <Plus className={`h-6 w-6 ${dragActive ? 'text-primary' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {dragActive ? 'Bilder hier ablegen' : 'Bilder hinzufügen'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Drag & Drop oder klicken zum Auswählen
                </p>
              </div>
            </div>
          </div>

          {/* Upload preview */}
          {uploadedImages.length > 0 && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  {uploadedImages.length} Bild{uploadedImages.length > 1 ? 'er' : ''} ausgewählt
                </p>
                <Button onClick={uploadImages} disabled={isUploading} size="sm" className="gap-1.5 h-8 bg-primary shadow-sm shadow-primary/20">
                  {isUploading ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Hochladen…</>) : (<><Upload className="h-3.5 w-3.5" /> Hochladen</>)}
                </Button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group aspect-square">
                    <img src={preview} alt={`Vorschau ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                    <button onClick={(e) => { e.stopPropagation(); setUploadedImages(p => p.filter((_, i) => i !== index)); setImagePreviews(p => p.filter((_, i) => i !== index)); }}
                      className="absolute top-1 right-1 h-6 w-6 bg-black/60 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors">
                      <XCircle className="h-3.5 w-3.5 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing gallery grid */}
          <div className="mt-6">
            {isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : images.length === 0 ? (
              <div className="text-center py-10 rounded-xl bg-slate-50/50">
                <ImageIcon className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-500 font-medium">Noch keine Bilder vorhanden</p>
                <p className="text-xs text-slate-400 mt-1">Laden Sie Bilder hoch, um Ihr Restaurant zu präsentieren</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {images.map((image, index) => {
                  const isMain = mainImage && image.id === mainImage.id;
                  const isDeleting = isDeletingImage === String(image.id);
                  return (
                    <div key={image.id} className={`relative group rounded-xl overflow-hidden border-2 ${
                      isMain ? 'border-purple-300 ring-2 ring-purple-100 shadow-md' : 'border-slate-200/60'
                    }`}>
                      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                        <img
                          src={normalizeImagePath(image.path)}
                          alt={`Bild ${index + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder_header.png'; }}
                        />
                      </div>

                      {/* Always-visible badges */}
                      {isMain && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-purple-600/90 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 shadow-sm">
                            <Crown className="h-2.5 w-2.5 mr-0.5" /> Profil
                          </Badge>
                        </div>
                      )}

                      {/* Action buttons - always visible */}
                      <div className="absolute top-2 right-2 flex gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="h-7 w-7 rounded-lg bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center transition-colors">
                              <MoreVertical className="h-3.5 w-3.5 text-white" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onClick={() => setPreviewImage(normalizeImagePath(image.path))} className="gap-2 text-xs">
                              <Eye className="h-3.5 w-3.5" /> Vergrößern
                            </DropdownMenuItem>
                            {!isMain && (
                              <DropdownMenuItem onClick={() => setMainImageMutation.mutate({ restaurantId: restaurant.id, imageId: image.id })} className="gap-2 text-xs text-purple-600">
                                <Crown className="h-3.5 w-3.5" /> Als Profilbild
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => sortImagesMutation.mutate({ restaurantId: restaurant.id, imageId: image.id, direction: 'up' })} className="gap-2 text-xs">
                              <ArrowUp className="h-3.5 w-3.5" /> Nach vorne
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => sortImagesMutation.mutate({ restaurantId: restaurant.id, imageId: image.id, direction: 'down' })} className="gap-2 text-xs">
                              <ArrowDown className="h-3.5 w-3.5" /> Nach hinten
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => { setIsDeletingImage(String(image.id)); deleteImageMutation.mutate({ restaurantId: restaurant.id, imageId: image.id }); }}
                              className="gap-2 text-xs text-red-600 focus:text-red-600">
                              <Trash2 className="h-3.5 w-3.5" /> Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Deleting overlay */}
                      {isDeleting && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin text-red-500" />
                        </div>
                      )}

                      {/* Sort order badge */}
                      <div className="absolute bottom-2 left-2">
                        <span className="text-[10px] font-medium text-white bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded">
                          #{image.sortOrder || index + 1}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* === FULLSCREEN PREVIEW MODAL === */}
      {previewImage && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <button className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <XCircle className="h-6 w-6 text-white" />
          </button>
          <img src={previewImage} alt="Vorschau" className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default RestaurantFormMedia;
